(() => {
  'use strict';
  const TIMING = { entrance: .85, reveal: .72, stagger: .09 };
  const MOTION = { offset: 28, ease: 'power3.out', storage: 'erya-directions-motion' };
  const preference = matchMedia('(prefers-reduced-motion: reduce)');
  class CampaignSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-campaign-track]');
      this.slides = [...this.querySelectorAll('[data-campaign-slide]')];
      if (!this.track || !this.slides.length) return;
      this.index = 0;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.update = () => {
        this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth)));
        this.querySelector('[data-campaign-prev]').disabled = this.index === 0;
        this.querySelector('[data-campaign-next]').disabled = this.index === this.slides.length - 1;
        this.querySelector('[data-campaign-status]').textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
      };
      this.move = index => this.track.scrollTo({ left: this.track.clientWidth * Math.max(0, Math.min(index, this.slides.length - 1)), behavior: preference.matches || document.body.dataset.directionMotionState === 'paused' ? 'instant' : 'smooth' });
      this.querySelector('[data-campaign-prev]').addEventListener('click', () => this.move(this.index - 1), options);
      this.querySelector('[data-campaign-next]').addEventListener('click', () => this.move(this.index + 1), options);
      this.track.addEventListener('scroll', this.update, { ...options, passive: true });
      this.track.addEventListener('keydown', event => { if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); this.move(this.index + (event.key === 'ArrowRight' ? 1 : -1)); } }, options);
      this.editor = event => { const slide = event.target.closest('[data-campaign-slide]'); if (slide && this.contains(slide)) this.move(this.slides.indexOf(slide)); };
      document.addEventListener('shopify:block:select', this.editor, options);
      this.resize = new ResizeObserver(this.update); this.resize.observe(this.track);
      this.update();
      this.querySelector('[data-campaign-controls]').hidden = false;
    }
    disconnectedCallback() { this.controller?.abort(); this.resize?.disconnect(); }
  }
  class Lookbook extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.buttons = [...this.querySelectorAll('[data-look-select]')];
      this.panels = [...this.querySelectorAll('[data-look-product]')];
      this.select = index => { this.buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index))); this.panels.forEach((panel, i) => { panel.hidden = i !== index; }); };
      this.buttons.forEach((button, index) => button.addEventListener('click', () => this.select(index), { signal: this.controller.signal }));
      document.addEventListener('shopify:block:select', event => { const frame = event.target.closest('[data-look-frame]'); if (frame && this.contains(frame)) this.select(Number(frame.dataset.lookFrame)); }, { signal: this.controller.signal });
      this.select(0);
      this.buttons.forEach(button => { button.hidden = false; });
      const hint = this.querySelector('[data-look-hint]');
      if (hint) hint.hidden = false;
    }
    disconnectedCallback() { this.controller?.abort(); }
  }
  if (!customElements.get('erya-campaign-slider')) customElements.define('erya-campaign-slider', CampaignSlider);
  if (!customElements.get('erya-lookbook')) customElements.define('erya-lookbook', Lookbook);
  let context;
  let initialized = false;
  let paused = false;
  try { paused = localStorage.getItem(MOTION.storage) === 'paused'; } catch (_) {}
  const initMotion = () => {
    context?.revert();
    const playEntrance = !initialized;
    initialized = true;
    const stopped = paused || preference.matches;
    document.body.dataset.directionMotionState = stopped ? 'paused' : 'active';
    document.querySelectorAll('button[data-direction-motion]').forEach(button => { button.hidden = !window.gsap || !window.ScrollTrigger; button.disabled = preference.matches; button.setAttribute('aria-pressed', String(stopped)); button.textContent = stopped ? document.body.dataset.directionResume : document.body.dataset.directionPause; });
    if (stopped || !window.gsap || !window.ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);
    context = gsap.context(() => {
      // Reinitializing after an editor change, motion toggle, or bfcache restore
      // must never hide content that the visitor has already reached.
      if (!playEntrance) return;
      gsap.utils.toArray('[data-direction-title]').forEach(element => gsap.from(element, { y: MOTION.offset, opacity: 0, duration: TIMING.entrance, ease: MOTION.ease, clearProps: 'transform,opacity' }));
      gsap.utils.toArray('[data-direction-reveal], .erya-section-heading > *, .erya-product-card, .erya-category, .erya-story__content > *, .erya-guide > *').forEach(element => {
        gsap.from(element, { y: MOTION.offset, opacity: 0, duration: TIMING.reveal, ease: MOTION.ease, scrollTrigger: { trigger: element, start: 'top 94%', once: true }, clearProps: 'transform,opacity' });
      });
      // Category panels can be switched while outside the viewport. Keep them
      // fully opaque so activating a tab always exposes readable content.
      gsap.utils.toArray('[data-commerce-reveal]').forEach(element => {
        gsap.from(element, { y: MOTION.offset, duration: TIMING.reveal, ease: MOTION.ease, scrollTrigger: { trigger: element, start: 'top 94%', once: true }, clearProps: 'transform' });
      });
    });
    ScrollTrigger.refresh();
  };
  document.addEventListener('click', event => { if (!event.target.closest('button[data-direction-motion]')) return; paused = !paused; try { localStorage.setItem(MOTION.storage, paused ? 'paused' : 'active'); } catch (_) {} initMotion(); });
  document.addEventListener('erya:category:change', event => {
    const panel = event.detail?.panel;
    if (!panel || !window.gsap || !window.ScrollTrigger) return;
    ScrollTrigger.getAll().forEach(trigger => { if (trigger.trigger === panel) trigger.kill(); });
    gsap.killTweensOf(panel);
    gsap.set(panel, { clearProps: 'transform' });
    ScrollTrigger.refresh();
  });
  preference.addEventListener('change', initMotion);
  document.addEventListener('shopify:section:load', initMotion);
  document.addEventListener('shopify:section:unload', () => { context?.revert(); requestAnimationFrame(initMotion); });
  addEventListener('pagehide', () => context?.revert());
  addEventListener('pageshow', event => { if (event.persisted) initMotion(); });
  addEventListener('load', () => window.ScrollTrigger?.refresh(), { once: true });
  document.fonts?.ready.then(() => window.ScrollTrigger?.refresh());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initMotion, { once: true }); else initMotion();
})();
